import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, Truck, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  phone_number: "",
  email: "",
  shipping_address: "",
  product_type: "",
  customer_note: "",
};

const GUARANTEES = [
  { icon: UserCheck, text: "Every submission reviewed personally by our team" },
  { icon: Truck, text: "Delivery arranged to your specified address" },
  { icon: Lock, text: "Your details are kept strictly confidential" },
];

export default function OrderFormSection({ prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, product_type: prefill }));
    }
  }, [prefill]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        ...form,
        customer_note: form.customer_note || null,
      });
      setSuccessId(res.data.order_id);
      toast.success("Order submitted successfully");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setSuccessId(null);
  };

  return (
    <section
      id="order"
      data-testid="order-section"
      className="py-20 lg:py-28 relative overflow-hidden"
    >
      <div className="absolute top-20 -left-32 h-[380px] w-[380px] rounded-full bg-taupe-300/25 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="eyebrow" data-testid="order-eyebrow">
            Quick Order
          </span>
          <h2 className="mt-4 font-poppins font-semibold text-2xl sm:text-3xl text-charcoal-900">
            Place your order, we handle the rest
          </h2>
          <p className="mt-6 text-sm sm:text-base text-charcoal-700 leading-relaxed max-w-lg">
            Complete the form and your request is delivered straight to our
            procurement desk. Specify any product type — if you can describe
            it, we can source it.
          </p>
          <ul className="mt-10 space-y-5">
            {GUARANTEES.map((g) => (
              <li key={g.text} className="flex items-start gap-4">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-cream-100 border border-cream-200">
                  <g.icon className="h-4 w-4 text-taupe-600" />
                </span>
                <span className="text-sm text-charcoal-700 leading-relaxed pt-2">
                  {g.text}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="bg-cream-100 border border-cream-200 rounded-2xl p-7 sm:p-10 shadow-[0_32px_64px_-32px_rgba(126,98,68,0.35)]"
        >
          {successId ? (
            <div data-testid="order-success-panel" className="text-center py-10">
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#2E6B4F]" />
              <h3 className="mt-6 font-poppins font-semibold text-2xl text-charcoal-900">
                Order Received
              </h3>
              <p className="mt-3 text-sm text-charcoal-700">
                Reference:{" "}
                <span className="font-semibold text-taupe-700">{successId}</span>
              </p>
              <p className="mt-4 text-sm text-charcoal-700 leading-relaxed max-w-sm mx-auto">
                Thank you. Your order details have been sent to our team — we
                will contact you shortly to confirm.
              </p>
              <button
                data-testid="order-another-button"
                onClick={reset}
                className="mt-8 bg-charcoal-900 text-cream-50 text-xs uppercase tracking-[0.2em] font-medium px-7 py-3.5 rounded-full hover:bg-taupe-700 transition-colors duration-300"
              >
                Place Another Order
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="order-form" className="space-y-5">
              <div>
                <label htmlFor="full_name" className="field-label">
                  Full Name *
                </label>
                <input
                  id="full_name"
                  data-testid="order-input-full-name"
                  required
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="e.g. Ahmad bin Abdullah"
                  className="field-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="phone_number" className="field-label">
                    Phone Number *
                  </label>
                  <input
                    id="phone_number"
                    data-testid="order-input-phone"
                    required
                    type="tel"
                    value={form.phone_number}
                    onChange={set("phone_number")}
                    placeholder="e.g. +6012-345 6789"
                    className="field-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="field-label">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    data-testid="order-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className="field-input"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="shipping_address" className="field-label">
                  Delivery Address *
                </label>
                <textarea
                  id="shipping_address"
                  data-testid="order-input-address"
                  required
                  rows={3}
                  value={form.shipping_address}
                  onChange={set("shipping_address")}
                  placeholder="Full delivery address including postcode and state"
                  className="field-input resize-none"
                />
              </div>
              <div>
                <label htmlFor="product_type" className="field-label">
                  Product Type *
                </label>
                <input
                  id="product_type"
                  data-testid="order-input-product-type"
                  required
                  value={form.product_type}
                  onChange={set("product_type")}
                  placeholder="Specify the items you require, e.g. 50 units of office chairs"
                  className="field-input"
                />
              </div>
              <div>
                <label htmlFor="customer_note" className="field-label">
                  Note (Message / Concerns)
                </label>
                <textarea
                  id="customer_note"
                  data-testid="order-input-note"
                  rows={3}
                  value={form.customer_note}
                  onChange={set("customer_note")}
                  placeholder="Any special instructions, questions or concerns for our team"
                  className="field-input resize-none"
                />
              </div>
              <button
                data-testid="order-form-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-charcoal-900 text-cream-50 text-xs uppercase tracking-[0.22em] font-medium px-7 py-4 rounded-full hover:bg-taupe-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Submit Order"
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
